import { makeObservable, observable, action, runInAction } from 'mobx';
import serviceService from '../services/serviceService';
import { formatLocation } from '../utils/locationFormat';

class ServiceStore {
  // Observable state
  categories = [];
  cities = [];
  cityToOriginal = {}; // Maps normalized city to original value for backend filtering
  services = [];
  selectedService = null;
  availableSlots = null;
  loading = false;
  error = null;

  // Pagination
  currentPage = 1;
  totalCount = 0;
  pageSize = 20;

  // Filters
  filters = {
    service_category: '',
    partner__city: '',
    animal_category: '',
    search: '',
    ordering: '',
    min_rating: '',
    partner: ''
  };

  constructor(rootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      categories: observable,
      cities: observable,
      services: observable,
      selectedService: observable,
      availableSlots: observable,
      loading: observable,
      error: observable,
      currentPage: observable,
      totalCount: observable,
      filters: observable,
      fetchServiceCategories: action,
      fetchCities: action,
      fetchServices: action,
      fetchServiceDetail: action,
      fetchAvailableSlots: action,
      setFilter: action,
      clearFilters: action,
      clearError: action,
      clearAvailableSlots: action
    });
  }

  /**
   * Fetch all service categories
   */
  async fetchServiceCategories() {
    this.loading = true;
    this.error = null;

    const result = await serviceService.fetchServiceCategories();

    runInAction(() => {
      if (result.success) {
        this.categories = result.data.results || result.data;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Fetch cities where services are available
   */
  async fetchCities() {
    const result = await serviceService.fetchCities();

    runInAction(() => {
      if (result.success) {
        // Normalize cities and remove duplicates (case-insensitive)
        // Keep mapping to original values for backend filtering
        const cityMap = new Map();
        (result.data || []).forEach(city => {
          if (city) {
            const normalized = formatLocation(city);
            if (!cityMap.has(normalized)) {
              cityMap.set(normalized, city); // Store original value
            }
          }
        });
        this.cities = [...cityMap.keys()].sort();
        this.cityToOriginal = Object.fromEntries(cityMap);
      }
    });

    return result.success;
  }

  /**
   * Fetch services with current filters
   * @param {number} page - Page number
   */
  async fetchServices(page = 1) {
    this.loading = true;
    this.error = null;

    const params = {
      ...this.filters,
      page
    };

    // Convert normalized city to original value for backend
    if (params.partner__city && this.cityToOriginal[params.partner__city]) {
      params.partner__city = this.cityToOriginal[params.partner__city];
    }

    // Remove empty filters
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === null || params[key] === undefined) {
        delete params[key];
      }
    });

    const result = await serviceService.fetchServices(params);

    runInAction(() => {
      if (result.success) {
        this.services = result.data.results || [];
        this.totalCount = result.data.count || 0;
        this.currentPage = page;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Fetch service detail by ID
   * @param {number} serviceId
   */
  async fetchServiceDetail(serviceId) {
    this.loading = true;
    this.error = null;

    const result = await serviceService.fetchServiceDetail(serviceId);

    runInAction(() => {
      if (result.success) {
        this.selectedService = result.data;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Fetch available time slots for a service
   * @param {number} serviceId
   * @param {string} date - Date in YYYY-MM-DD format
   */
  async fetchAvailableSlots(serviceId, date) {
    this.loading = true;
    this.error = null;

    const result = await serviceService.fetchAvailableSlots(serviceId, date);

    runInAction(() => {
      if (result.success) {
        this.availableSlots = result.data;
      } else {
        this.error = result.error;
        this.availableSlots = null;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Set a filter value
   * @param {string} key - Filter key
   * @param {any} value - Filter value
   */
  setFilter(key, value) {
    this.filters[key] = value;
  }

  /**
   * Clear all filters
   */
  clearFilters() {
    this.filters = {
      service_category: '',
      partner__city: '',
      animal_category: '',
      search: '',
      ordering: '',
      min_rating: '',
      partner: ''
    };
  }

  /**
   * Clear error message
   */
  clearError() {
    this.error = null;
  }

  /**
   * Clear available slots
   */
  clearAvailableSlots() {
    this.availableSlots = null;
  }
}

export default ServiceStore;
